import type {
  ILearningMenuLink,
  INestedMenuLink,
} from '@crehana/compass.layouts';
import { useB2bRoutes } from '@crehana/compass.layouts';
import { getCurrentUrlItem, useProductInfo } from '@crehana/layouts';
import { useStudentUrls } from '@crehana/web';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { useLocation } from 'react-router-dom';

import type { TPageLayoutBreadcrumbItem } from '@/shared/vendor/compass-web-page-layout';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';

import { useCrehanaTranslation } from './useCrehanaTranslation';

type TTrailMatch = {
  group?: ILearningMenuLink;
  leaf: ILearningMenuLink | INestedMenuLink;
};

/**
 * Walks the flat 2-level `b2bRoutes` tree (the SAME source the sidebar
 * itself renders from) looking for the entry that matches `pathname`.
 *
 * Does an exact-match pass over the ENTIRE tree first (all top-levels +
 * their nested children), and only falls back to a `startsWith` match if no
 * exact match exists anywhere. This avoids a short url like `/courses`
 * greedily matching before a more specific nested url is checked.
 */
const findTrail = (
  routes: ILearningMenuLink[],
  pathname: string,
): TTrailMatch | undefined => {
  // Pass 1: exact match, nested children first (more specific), then
  // top-level-only routes (no `nested`).
  for (const top of routes) {
    if (top.nested?.length) {
      const exactChild = top.nested.find(child => child.url === pathname);
      if (exactChild) {
        return { group: top, leaf: exactChild };
      }
    } else if (top.url && top.url === pathname) {
      return { group: undefined, leaf: top };
    }
  }

  // Pass 2: fallback to `startsWith`, same precedence order.
  for (const top of routes) {
    if (top.nested?.length) {
      const partialChild = top.nested.find(
        child => !!child.url && pathname.startsWith(child.url),
      );
      if (partialChild) {
        return { group: top, leaf: partialChild };
      }
    } else if (top.url && pathname.startsWith(top.url)) {
      return { group: undefined, leaf: top };
    }
  }

  return undefined;
};

/**
 * Resolves the white-labeled "learn" product-group header rendered by the
 * sidebar above the `useB2bRoutes` tree — it is NOT part of `useB2bRoutes`'s
 * output. Since `@crehana/layouts@77.15.0` this app also mounts
 * `ProductInfoProvider` (see `ProductInfoProviderBridge` in
 * `LearnDashboard/context`), which exposes `sidebarItems.productItems` — the
 * SAME white-labeled top-level module list the org's sidebar renders from
 * (backed by `organization_settings.get_sidebar`).
 *
 * There is no stable `sectionKey`/`id` to match this app's own module against
 * (no confirmed GraphQL fixture or sibling reference for one) — so, same
 * approach as `@crehana/compass.web`'s own `useDashboardBreadcrumb`, this
 * matches by URL: `getCurrentUrlItem` walks `productItems` doing a
 * longest-prefix match against the current pathname, and the FIRST item in
 * the returned trail is the top-level module the current route lives under.
 * Falls back to the `LABEL_BREADCRUMB_PRODUCT_SEGMENT` i18n default
 * ("Aprendizaje") when the provider hasn't loaded yet or no sidebar item
 * matches the current URL — same graceful-degradation style as the `!trail`
 * early-return below.
 */
const useProductSegmentLabel = (pathname: string, fallback: string): string => {
  const { sidebarItems } = useProductInfo();

  if (!sidebarItems) {
    return fallback;
  }

  const [topLevelItem] = getCurrentUrlItem(sidebarItems.productItems, pathname);

  return topLevelItem?.label || fallback;
};

type UseLearnBreadcrumbTrailReturn = {
  items: TPageLayoutBreadcrumbItem[] | undefined;
  isLoading: boolean;
};

/**
 * Builds the full sidebar breadcrumb trail ("Inicio / <product> / <group> /
 * <leaf>") for the current route, sourced from `useB2bRoutes` — the same
 * source the sidebar itself renders from — instead of a hardcoded 2-item
 * array. `<product>` is white-label-aware (see `useProductSegmentLabel`
 * above).
 */
const useLearnBreadcrumbTrail = (): UseLearnBreadcrumbTrailReturn => {
  // `useLocation()` is kept as the REACTIVE trigger: the hook must re-run on
  // every react-router-dom v5 navigation (the primary in-SPA nav for /learn
  // routes goes through `history.push`), so subscribing to the router context
  // is what makes the trail recompute on route change.
  //
  // BUT the pathname we MATCH against the sidebar `url`s is read from
  // `window.location.pathname`, not from `useLocation()`. Under the Next.js
  // host, react-router's `useLocation().pathname` can drift from the real
  // absolute URL (basename / next-driven navigations RRv5 doesn't observe).
  // Since `findTrail` matches ABSOLUTE sidebar urls (`/org/<slug>/learn/...`),
  // any drift makes every match fail → trail degrades to "Inicio / learn" on
  // every page, which reads as a breadcrumb that "does not refresh".
  // `window.location.pathname` is always the current absolute URL. SSR has no
  // `window` → fall back to the router pathname (hydration reconciles).
  // See CORE-1671 playbook §5.2c.
  const { pathname: routerPathname } = useLocation();
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : routerPathname;
  const { homeUrl } = useStudentUrls();
  const { t } = useCrehanaTranslation();
  const { me, organizationSlug, organizationId } = useLearnDashboardContext();

  // NOTE: `routesExtraData` is intentionally omitted. It only feeds
  // badges/counts onto menu items — the breadcrumb needs just labels + urls,
  // and importing `useRoutesExtraData` here creates a circular dependency
  // (useRoutesExtraData → AcademiesSection → RevampListTrack → LearnTeamTracks
  // → useLearnBreadcrumbTrail). CI circular-deps check catches it.
  const { b2bRoutes, isLoading } = useB2bRoutes({
    organizationSlug: organizationSlug || '',
    productPlatform: 'learning',
    email: me?.email || '',
    organizationId: organizationId as number,
  });
  const productSegmentLabel = useProductSegmentLabel(
    pathname,
    t('LABEL_BREADCRUMB_PRODUCT_SEGMENT'),
  );

  if (isLoading) {
    return { items: undefined, isLoading: true };
  }

  const trail = findTrail(b2bRoutes, pathname);

  const homeCrumb: TPageLayoutBreadcrumbItem = {
    text: t('LABEL_SIDEBAR_ITEM_START'),
    href: homeUrl,
    navigationType: 'client-side',
  };
  const productCrumb: TPageLayoutBreadcrumbItem = {
    text: productSegmentLabel,
  };

  if (!trail) {
    // Route not found in the sidebar tree — degrade gracefully with a
    // partial trail instead of crashing.
    return {
      items: [homeCrumb, productCrumb],
      isLoading: false,
    };
  }

  const { group, leaf } = trail;

  return {
    items: [
      homeCrumb,
      productCrumb,
      ...(group ? [{ text: group.label }] : []),
      { text: leaf.label },
    ],
    isLoading: false,
  };
};

export default useLearnBreadcrumbTrail;
